import React, {useEffect, useState} from 'react';
import {LeaderboardEntry} from '../../../../incase-domain/analytics';
import {chunkArray} from '../../../../utils/array.utils';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import Button from '../../../../incase-domain/components/button/Button';

interface LeaderboardReportProps {
    leaderboard?: LeaderboardEntry[];
    pdfLayout?: boolean;
    isLoading: boolean;
    title: string;
}

function LeaderboardReport({leaderboard, pdfLayout, isLoading, title}: LeaderboardReportProps): JSX.Element | null {
    const containerRef = React.useRef<HTMLDivElement>(null);

    const noOfEntriesPerColumnForPdf = leaderboard?.length ? Math.ceil(leaderboard.length / 3) : 0;
    const noOfEntriesPerColumn = pdfLayout ? noOfEntriesPerColumnForPdf : 10;

    const noOfAllColumns = leaderboard?.length ? Math.ceil(leaderboard.length / noOfEntriesPerColumn) : 0;
    const [noOfShownColumns, setNoOfShownColumns] = useState(2);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const setColumns = (): void => {
            if (containerRef.current && !pdfLayout) {
                const minColumnWidth = 400;
                const width = containerRef.current.offsetWidth;
                const columns = Math.floor(width / minColumnWidth);
                containerRef.current.style.setProperty('--noOfColumns', columns.toString());
                setNoOfShownColumns(columns);
            }
        };

        setColumns();
        window.addEventListener('resize', setColumns);

        return () => {
            window.removeEventListener('resize', setColumns);
        };
    }, [pdfLayout]);

    const handleOffsetChange = (offset: number): void => {
        if (offset < 0 || offset > noOfAllColumns - noOfShownColumns) return;
        containerRef.current?.style.setProperty('--offset', offset.toString());
        setOffset(offset);
    };

    if (!leaderboard?.length) return null;

    return (
        <div>
            <div className="p-presReport__card_title">{title}</div>
            <div className="p-presReport__card">
                <SkeletonLoader isLoading={isLoading} props={{style: {minHeight: '24rem'}}}>
                    {chunkArray(leaderboard, noOfEntriesPerColumn).map((group, i) => (
                        <div key={`leaderboard-group-${i}`} className="c-leaderboardReport__body_column">
                            {group?.map((user) => (
                                <div key={`leaderboard-entry-${user.id}`} className="c-leaderboardEntry u-flex u-h-between u-v-center">
                                    <div>{user.rank}.</div>
                                    <div className="c-leaderboardEntry__name">{user.name}</div>
                                    <div className="c-leaderboardEntry__score">{user.score}</div>
                                </div>
                            ))}
                        </div>
                    ))}
                </SkeletonLoader>
            </div>
            {!pdfLayout && noOfAllColumns > noOfShownColumns && (
                <div className="c-leaderboardReport__footer">
                    <Button
                        variant="ghost"
                        leading="ph:arrow-square-left-fill"
                        size="sm"
                        onClick={() => handleOffsetChange(offset - 1)}
                        disabled={offset === 0}
                    />
                    <Button
                        variant="ghost"
                        leading="ph:arrow-square-right-fill"
                        size="sm"
                        onClick={() => handleOffsetChange(offset + 1)}
                        disabled={offset >= noOfAllColumns - noOfShownColumns}
                    />
                </div>
            )}
        </div>
    );
}

export default LeaderboardReport;
